/**
 * `api.ts` runs outside the component tree (TanStack Query calls it), but a 401 has to bounce the
 * user to `/login`. Rather than threading a callback through every query, it's broadcast here and
 * picked up by the auth layer on mount — a tiny pub/sub channel.
 *
 * `pushToast` is a thin wrapper over react-toastify's own `toast()`, with an added optional
 * `variant`. It imports from `react-toastify/unstyled`, not the package root — the *styled*
 * entry's `<ToastContainer>` self-injects react-toastify's own default CSS as an unlayered
 * `<style>` tag on mount, which would beat our themed overrides in index.css regardless of import
 * order. `unstyled` has the identical API with that self-injection stripped. `App.tsx`'s
 * `<ToastContainer>` has to import from the same entry point so both talk to the same toast
 * registry.
 */
import { toast } from 'react-toastify/unstyled';

type ToastVariant = 'info' | 'success' | 'error' | 'warning';

type VoidListener = () => void;

const unauthorizedListeners = new Set<VoidListener>();

/** Most call sites are failure messages (a 429 warning or a rollback-after-error), so 'error' is
 *  the default variant. Callers with a happier message pass 'success' explicitly. */
export function pushToast(message: string, variant: ToastVariant = 'error'): void {
  toast(message, { type: variant });
}

export function subscribeToUnauthorized(listener: VoidListener): () => void {
  unauthorizedListeners.add(listener);
  return () => {
    unauthorizedListeners.delete(listener);
  };
}

export function reportUnauthorized(): void {
  for (const listener of unauthorizedListeners) {
    listener();
  }
}
